
























































































































































































































import {
  ComBoxSensorType,
  isBleResistanceSensorType,
  ROOM_SENSOR_INDEX,
} from "@/data/models/ComBoxSensorData";
import {
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import {
  getComBoxSensorForSensorIndex,
  getIresDeviceSensorColor,
  getIresDeviceSensorDescription,
  getIresDeviceSensorValueDescription,
  getSensorIndexForIresDeviceSensor,
  IresSensorAndValueCombination,
  IresDeviceSensor,
  IresDeviceSensorValue,
  iresSensorAndValueToKey,
} from "@/data/models/IresDeviceSensor";
import { aggregateSensorSlotSerials, calculateSlotNumberFromIndex, formatSensorSlotLabel, getSensorWarningsFromFlags, getSerialForSensorIndex } from "@/data/models/ComBox";
import { getPanelTypeFromSerialNumber, PanelType } from "@/data/models/Panel";
import { ProgramChain } from "@/data/models/ProgramChain";
import { Component, Vue, Watch, Prop, Ref } from "vue-property-decorator";
import {
  SensorLegendMetadata,
  formatSensorLegendBatteryStatus,
} from "@/data/models/SensorLegendMetadata";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { Sensor, SensorStatus } from "@/data/models/Sensor";
import { getSensor } from "@/data/repository/SensorRepository";

import {
  COLOR_DRYING_GRAPH_LEGEND_BACKGROUND,
  COLOR_DRYING_GRAPH_LEGEND_BACKGROUND_DARK,
  COLOR_SENSOR_USAGE_MEASUREMENT,
  COLOR_SENSOR_USAGE_PROGRAM,
  COLOR_SENSOR_USAGE_REFERENCE,
  COLOR_SENSOR_BADGE_WARNING,
  COLOR_SENSOR_BADGE_INFO,
  PHASE_ANNOTATION_COLORS,
  COLOR_DRYING_GRAPH_LEGEND_HEADER_DARK,
} from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { getActiveUser } from "@/data/repository/UserRepository";
import { viewSensorDiagnosticsAccess, viewPowerInformationAccess, viewWeatherInformationAccess } from "@/permissions/UserPermissions";
import { toggleWeatherInformationDetailsLevel } from "@/utils/DryingProgressGraphDataExtractor";
import { WeatherInformationDetailsLevel } from "@/utils/DryingGraphYAxis";
import { getEffectiveUser } from "@/utils/ViewAs";

interface Cell {
  columnIndex: number;
  rowIndex: number;
  sensorIndex: number;
  sensorValueIndex: number;
  key: number;
}

export interface TargetSensors {
  chainId: string;
  showPhases: boolean;
  selectedCombinations: IresSensorAndValueCombination[];
}

export enum LegendDisplayMode {
  HIDDEN = 0,
  OVERLAY = 1,
  BELOW = 2,
}

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
  },
})
export default class SelectionTableComponent extends Vue {
  @Ref("sensorInfoDialog")
  sensorInfoDialogComponent!: BaseDialogComponent;
  @Prop() isShowingComBoxDetails!: boolean;
  @Prop({ default: null }) projectId!: string | null;
  @Prop({ default: () => new Map() }) sensorLegendMetadata!: Map<number, SensorLegendMetadata>;
  @Prop({ default: false }) isMini!: boolean;

  /** Fallback sensor slot serials from comBoxStatus — only passed for active dryings. */
  @Prop({ default: undefined }) activeFallbackSerials!: Record<string, string> | undefined;

  /** Live per-slot sensor flags from comBoxStatus — only passed for active dryings. */
  @Prop({ default: undefined }) activeSensorFlags!: number[] | undefined;

  /**
   * Whether the displayed chain is the device's running drying.
   *
   * Sensor info (both badges and the dialog) is shown only then. A finished drying has no live
   * sensor state to report, and reconstructing one from the event log was decided against: the
   * replay can only be as complete as the events its query returned, and it cannot clear a
   * battery-weak warning at all, so it produced warnings that had long since resolved.
   */
  @Prop({ default: false }) isActiveDrying!: boolean;

  /** Aggregated sensor slot serials from program chain runs. Populated internally. */
  private aggregatedSensorSlotSerials: Record<string, string> = {};

  sensorInfoDialogData: SensorLegendMetadata | null = null;

  /**
   * `sensors/{serial}` document for the open dialog: null while loading, and when the sensor has
   * no document (nothing registered it, and no ComBox has reported it since auto-creation shipped).
   */
  private sensorDocument: Sensor | null = null;

  /** Serial the open dialog is showing, so a slow read cannot land on the next sensor opened. */
  private openSensorSerial: string | null = null;
  private sensorTypeMap = new Map<number, ComBoxSensorType>();

  /** Maps event 77 warning keys to sensorInfo status i18n keys. */
  private static readonly WARNING_TO_STATUS_KEY: Record<string, string> = {
    sensorBatteryWeak: "statusBatteryLow",
    sensorLinkWeak: "statusPoorLink",
    sensorLinkTimeout: "statusSensorLost",
    sensorTurnedOff: "statusTurnedOff",
  };

  /** Severity order for warning statuses (higher = more severe, shown first). */
  private static readonly STATUS_SEVERITY: Record<string, number> = {
    statusTurnedOff: 4,
    statusSensorLost: 3,
    statusPoorLink: 2,
    statusBatteryLow: 1,
  };

  chainId = "";

  sensorWarningColor = COLOR_SENSOR_BADGE_WARNING;
  sensorInfoColor = COLOR_SENSOR_BADGE_INFO;
  legendDisplayMode = LegendDisplayMode.OVERLAY;

  /** Legend panel background: light grey in light mode, dark elevated surface in dark mode. */
  /** Row and column header buttons: Vuetify's dark grey in light mode, a step above the card in dark mode. */
  get legendHeaderColor() {
    return themed("grey darken-1", COLOR_DRYING_GRAPH_LEGEND_HEADER_DARK);
  }

  /** The phases header button (white label): Vuetify's grey in light mode, the header shade in dark mode. */
  get phasesHeaderColor() {
    return themed("grey", COLOR_DRYING_GRAPH_LEGEND_HEADER_DARK);
  }

  get cardBackgroundColor() {
    return themed(
      COLOR_DRYING_GRAPH_LEGEND_BACKGROUND,
      COLOR_DRYING_GRAPH_LEGEND_BACKGROUND_DARK
    );
  }
  externalSensorCount = 0;

  phaseAnnoationColors = PHASE_ANNOTATION_COLORS;

  showPhases = true;
  showPowerLine = false;
  weatherInformationDetailsLevel = WeatherInformationDetailsLevel.NONE;

  /** External override from parent — syncs level back when weather fetch fails. */
  @Prop({ default: undefined })
  externalWeatherLevel!: WeatherInformationDetailsLevel | undefined;

  /** When true, the weather icon blinks to indicate data is loading. */
  @Prop({ default: false })
  isWeatherLoading!: boolean;

  /**
   * Whether to offer the weather toggle at all, on top of the capability check.
   *
   * For a screen that shows the legend but does not wire weather up: the RENO review stepper draws the
   * graph without coordinates, so the icon there toggled nothing. Defaults to true, so every other
   * caller keeps the icon.
   */
  @Prop({ default: true }) showWeatherOption!: boolean;

  @Watch("externalWeatherLevel")
  onExternalWeatherLevelChanged(newLevel: WeatherInformationDetailsLevel | undefined) {
    if (newLevel !== undefined && newLevel !== this.weatherInformationDetailsLevel) {
      this.weatherInformationDetailsLevel = newLevel;
    }
  }

  sensorUsageMap = new Map<IresDeviceSensor, string>();

  /** Whether the mouse is currently over the weather icon. */
  private isWeatherHovered = false;

  /** Whether the mouse is currently over the power icon. */
  private isPowerHovered = false;

  get showWeatherInformation() {
    return this.weatherInformationDetailsLevel !== WeatherInformationDetailsLevel.NONE;
  }

  onTogglePowerInformation() {
    this.showPowerLine = !this.showPowerLine;

    this.$emit("onShowPowerInformationChanged", this.showPowerLine);

    if (this.isPowerHovered) {
      if (this.showPowerLine) {
        this.onPowerHover();
      } else {
        this.onLegendHoverEnd();
      }
    }
  }

  onToggleWeatherInformation() {
    this.weatherInformationDetailsLevel = toggleWeatherInformationDetailsLevel(this.weatherInformationDetailsLevel);

    this.$emit("onWeatherInformationDetailsLevelChanged", this.weatherInformationDetailsLevel);

    if (this.isWeatherHovered) {
      if (this.showWeatherInformation) {
        this.onWeatherHover();
      } else {
        this.onLegendHoverEnd();
      }
    }
  }

  onToggleShowPhases() {
    this.showPhases = !this.showPhases;
  }

  getSquareColor(index: number) {
    return this.showPhases
      ? this.phaseAnnoationColors[index % this.phaseAnnoationColors.length]
      : "transparent";
  }

  /**
   * Cycles the legend: **overlay → collapsed → below → overlay**.
   *
   * Deliberately not the enum's own order, which would put "below" between the other two: collapsing is
   * what a reviewer reaches for while reading the graph, so it comes one press away from overlay, and
   * moving the legend under the graph is the rarer, layout-changing step. Requested 2026-08-06.
   */
  onToggleLegendMode() {
    switch (this.legendDisplayMode) {
      case LegendDisplayMode.OVERLAY:
        this.legendDisplayMode = LegendDisplayMode.HIDDEN;
        break;
      case LegendDisplayMode.HIDDEN:
        // The fallback keeps the ring correct as a two-state toggle if "below" is ever restricted again.
        this.legendDisplayMode = this.isBelowModeAvailable
          ? LegendDisplayMode.BELOW
          : LegendDisplayMode.OVERLAY;
        break;
      case LegendDisplayMode.BELOW:
        this.legendDisplayMode = LegendDisplayMode.OVERLAY;
        break;
    }
    this.applySensorSortOrder();
    this.$emit("onLegendDisplayModeChanged", this.legendDisplayMode);
  }

  @Prop({ default: false })
  showPhasesOption!: boolean;

  @Watch("showPhases")
  onShowPhasesChanged() {
    this.onSelectionChanged();
  }

  selectionOptionsSensor: IresDeviceSensor[] = [];
  selectionOptionsSensorValue: IresDeviceSensorValue[] = [];

  /** Sensors in natural slot order (by enum value). Used to re-sort when legend mode changes. */
  private naturalSensorOrder: IresDeviceSensor[] = [];

  selection: boolean[][] = [];
  selectionChangeTrigger = 0;

  disabledCombinations = new Set<number>();

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToViewPowerInformation() {
    return viewPowerInformationAccess(this.activeUser);
  }

  get isAllowedToViewWeatherInformation() {
    return viewWeatherInformationAccess(this.activeUser);
  }
 
  get showWeatherIcon() {
    return (
      this.showWeatherOption &&
      this.isLegendVisible &&
      this.isAllowedToViewWeatherInformation
    );
  }

  get isLegendVisible() {
    return this.legendDisplayMode !== LegendDisplayMode.HIDDEN;
  }

  /** Whether the below-graph legend mode is available. Always enabled for all sensor counts. */
  get isBelowModeAvailable(): boolean {
    return true;
  }

  get toggleLegendModeIcon(): string {
    switch (this.legendDisplayMode) {
      case LegendDisplayMode.HIDDEN:
        return "mdi-dock-window";
      case LegendDisplayMode.OVERLAY:
        return "mdi-dock-top";
      case LegendDisplayMode.BELOW:
      default:
        return "mdi-dock-bottom";
    }
  }

  showToggleCell(cell: Cell) {
    const isToggleCell = cell.columnIndex > 0 && cell.rowIndex > 0;

    return (
      isToggleCell &&
      !this.isSensorCombinationDisabled(cell.sensorIndex, cell.sensorValueIndex)
    );
  }

  isSensorCombinationDisabled(sensorIndex: number, sensorValueIndex: number) {
    return this.disabledCombinations.has(
      iresSensorAndValueToKey({
        sensor: this.selectionOptionsSensor[sensorIndex],
        value: this.selectionOptionsSensorValue[sensorValueIndex],
      })
    );
  }

  onToggleCellSelection(cell: Cell) {
    if (cell.columnIndex == 0 || cell.rowIndex == 0) {
      //Not a toggle cell
      return;
    }

    if (this.isSensorCombinationDisabled(cell.sensorIndex, cell.sensorValueIndex)) {
      return;
    }

    this.selection[cell.sensorIndex][
      cell.sensorValueIndex
    ] = !this.isCellSelected(cell);

    this.onSelectionChanged();
  }

  isCellSelected(cell: Cell) {
    if (this.isSensorCombinationDisabled(cell.sensorIndex, cell.sensorValueIndex)) {
      return false;
    }

    return this.selection[cell.sensorIndex][cell.sensorValueIndex];
  }

  onToogleRowSelection(cell: Cell) {
    const eligibleSensorIndexes = this.selectionOptionsSensor
      .map((_, index) => index)
      .filter(
        (index) => !this.isSensorCombinationDisabled(index, cell.sensorValueIndex)
      );

    if (eligibleSensorIndexes.length === 0) {
      return;
    }

    const selectAll = eligibleSensorIndexes.some(
      (index) => !this.selection[index][cell.sensorValueIndex]
    );

    eligibleSensorIndexes.forEach(
      (index) => (this.selection[index][cell.sensorValueIndex] = selectAll)
    );

    this.onSelectionChanged();
  }

  onToggleColumnSelection(cell: Cell) {
    const eligibleValueIndexes = this.selectionOptionsSensorValue
      .map((_, index) => index)
      .filter(
        (index) => !this.isSensorCombinationDisabled(cell.sensorIndex, index)
      );

    if (eligibleValueIndexes.length === 0) {
      return;
    }

    const selectAll = eligibleValueIndexes.some(
      (index) => !this.selection[cell.sensorIndex][index]
    );

    eligibleValueIndexes.forEach(
      (index) => (this.selection[cell.sensorIndex][index] = selectAll)
    );

    this.onSelectionChanged();
  }

  onSelectionChanged() {
    this.selectionChangeTrigger++;
    const selectedCombinations: IresSensorAndValueCombination[] = [];

    this.selection.forEach((col, sensorIndex) => {
      col.forEach((isSelected, sensorValueIndex) => {
        if (isSelected && !this.isSensorCombinationDisabled(sensorIndex, sensorValueIndex)) {
          selectedCombinations.push({
            sensor: this.selectionOptionsSensor[sensorIndex],
            value: this.selectionOptionsSensorValue[sensorValueIndex],
          });
        }
      });
    });

    this.$emit("onTargetSensorsChanged", {
      chainId: this.chainId,
      showPhases: this.showPhases,
      selectedCombinations: selectedCombinations,
    });
  }

  isTopLeft(cell: Cell) {
    return cell.columnIndex === 0 && cell.rowIndex === 0;
  }

  isRowHeader(cell: Cell) {
    return cell.columnIndex === 0 && cell.rowIndex !== 0;
  }

  isColumnHeader(cell: Cell) {
    return cell.columnIndex !== 0 && cell.rowIndex === 0;
  }

  getRowHeaderDescription(cell: Cell) {
    return this.getSensorValueDescription(
      this.selectionOptionsSensorValue[cell.rowIndex - 1]
    );
  }

  getColumnHeaderDescription(cell: Cell) {
    return this.getSensorDescription(
      this.selectionOptionsSensor[cell.columnIndex - 1]
    );
  }

  getColumnHeaderColor(cell: Cell): string {
    const sensor = this.selectionOptionsSensor[cell.columnIndex - 1];
    const usage = this.sensorUsageMap.get(sensor);
    switch (usage) {
      case "MEASUREMENT": return COLOR_SENSOR_USAGE_MEASUREMENT;
      case "PROGRAM": return COLOR_SENSOR_USAGE_PROGRAM;
      case "REFERENCE": return COLOR_SENSOR_USAGE_REFERENCE;
      default: return "white";
    }
  }

  /** Returns the sensor index for a column header cell. */
  private getSensorIndexForCell(cell: Cell): number | null {
    const sensor = this.selectionOptionsSensor[cell.columnIndex - 1];
    return getSensorIndexForIresDeviceSensor(sensor);
  }

  /** Whether sensor metadata (from event 77 snapshot) or hardware type info is available for this column. */
  hasSensorMetadata(cell: Cell): boolean {
    if (!this.isActiveDrying) return false;

    const sensorIndex = this.getSensorIndexForCell(cell);
    if (sensorIndex === null) return false;
    return this.sensorLegendMetadata.has(sensorIndex) || this.sensorTypeMap.has(sensorIndex);
  }

  /**
   * Active warning keys for a sensor index, read from the ComBox's live per-slot flags.
   *
   * The flags are the only source. They are passed only for the running drying, and only by a box
   * on firmware v4.0.0+; anything else has no warning to show rather than a reconstructed one.
   */
  private getSensorWarningKeys(sensorIndex: number): string[] {
    return getSensorWarningsFromFlags(this.activeSensorFlags?.[sensorIndex]);
  }

  /** Whether this sensor has active warnings. */
  hasSensorWarning(cell: Cell): boolean {
    if (!this.isActiveDrying) return false;

    const sensorIndex = this.getSensorIndexForCell(cell);
    if (sensorIndex === null) return false;
    return this.getSensorWarningKeys(sensorIndex).length > 0;
  }

  /** Opens the sensor info popup for the given column header. */
  onSensorInfoClick(cell: Cell) {
    // Both badges are already hidden for a finished drying; this keeps the dialog unreachable
    // rather than relying on that.
    if (!this.isActiveDrying) return;

    const sensorIndex = this.getSensorIndexForCell(cell);
    if (sensorIndex === null) return;

    let meta = this.sensorLegendMetadata.get(sensorIndex);
    if (!meta) {
      // Create synthetic metadata for sensors without event 77 data (wired/room sensors).
      // A live warning is enough on its own: a badge the user cannot click would be a dead end.
      if (
        !this.sensorTypeMap.has(sensorIndex) &&
        this.getSensorWarningKeys(sensorIndex).length === 0
      ) {
        return;
      }
      meta = {
        sensorIndex,
        slotNumber: calculateSlotNumberFromIndex(sensorIndex),
      };
    }

    meta.sensorType = this.sensorTypeMap.get(sensorIndex);
    this.sensorInfoDialogData = meta;

    const serialNumber =
      getSerialForSensorIndex(this.aggregatedSensorSlotSerials, sensorIndex) ??
      meta.serialNumber;

    /* Read the sensor document for the rows the flags cannot supply. Deliberately not awaited:
       the dialog opens on the values already in hand and fills the rest in when the read lands,
       so a slow read never delays the popup. */
    this.sensorDocument = null;
    this.openSensorSerial = serialNumber ?? null;
    if (serialNumber) {
      getSensor(serialNumber).then((sensor) => {
        if (this.openSensorSerial === serialNumber) {
          this.sensorDocument = sensor;
        }
      });
    }

    const slotLabel = formatSensorSlotLabel(
      sensorIndex,
      meta.sensorType,
      serialNumber,
      this.isMini,
    ) ?? `${meta.slotNumber}L`;
    this.sensorInfoDialogComponent.show({
      title: (sensorIndex === ROOM_SENSOR_INDEX
        ? this.$t('sensorInfo.titleRoom')
        : this.$t('sensorInfo.title', [slotLabel])
      ).toString(),
      positiveButtonText: 'OK',
      negativeButtonText: null,
      persistent: false,
      maxWidth: 400,
    });
  }

  /** Whether the active user can see sensor diagnostics (signal strength, battery, firmware). */
  get isIresAdmin(): boolean {
    return viewSensorDiagnosticsAccess(getActiveUser());
  }

  /**
   * Returns warning statuses sorted by severity (most severe first).
   *
   * Same source as the badge, so the dialog can never contradict the icon that opened it.
   */
  get sortedWarningStatuses(): string[] {
    if (!this.sensorInfoDialogData) return [];
    return this.getSensorWarningKeys(this.sensorInfoDialogData.sensorIndex)
      .map(key => SelectionTableComponent.WARNING_TO_STATUS_KEY[key])
      .filter((key): key is string => !!key)
      .sort((a, b) =>
        (SelectionTableComponent.STATUS_SEVERITY[b] ?? 0) -
        (SelectionTableComponent.STATUS_SEVERITY[a] ?? 0)
      );
  }

  /** Status of the sensor document loaded for the open dialog, when there is one. */
  private get sensorDocumentStatus(): SensorStatus | undefined {
    return this.sensorDocument?.sensorStatus;
  }

  /*
   * The three diagnostics rows below prefer the sensor document and fall back to the event 77
   * snapshot. The document is the ComBox's last BLE reading for that sensor specifically; the
   * snapshot is what the box reported at the start of a dry run. Neither is always present: a
   * sensor with no document keeps working on snapshot values, and a chain with no snapshot event
   * (pre-v4 firmware) gets the document.
   */

  get displaySerialNumber(): string | undefined {
    return this.sensorDocument?.serialNo ?? this.sensorInfoDialogData?.serialNumber;
  }

  get displaySignalStrength(): number | string | undefined {
    return this.sensorDocumentStatus?.rssi ?? this.sensorInfoDialogData?.rssi;
  }

  get displayFirmwareVersion(): string | undefined {
    return (
      this.sensorDocumentStatus?.firmwareVersion ??
      this.sensorInfoDialogData?.firmwareVersion
    );
  }

  get formattedSensorBatteryStatus(): string | null {
    const batteryLevelPercent = this.sensorDocumentStatus?.batteryLevelPercent;

    return formatSensorLegendBatteryStatus(
      batteryLevelPercent !== undefined
        ? String(batteryLevelPercent)
        : this.sensorInfoDialogData?.batteryLevel
    );
  }

  /** Returns the sensor type category label (e.g. "Temp. / humidity sensor"). */
  private getSensorTypeCategoryText(): string {
    if (!this.sensorInfoDialogData) return '-';
    if (this.sensorInfoDialogData.sensorIndex === ROOM_SENSOR_INDEX) {
      return this.$t('sensorInfo.typeInternalSensor').toString();
    }
    if (!this.sensorInfoDialogData.sensorType) return '-';
    const typeKey = this.getSensorTypeKey(this.sensorInfoDialogData.sensorType);
    return typeKey ? this.$t('sensorInfo.' + typeKey).toString() : '-';
  }

  /** Returns the sensor connectivity label (e.g. "wired" or "wireless"). */
  private getSensorConnectivityText(): string {
    if (!this.sensorInfoDialogData) return '-';
    if (this.sensorInfoDialogData.sensorIndex === ROOM_SENSOR_INDEX) {
      return this.$t('sensorInfo.integrated').toString();
    }
    if (!this.sensorInfoDialogData.sensorType) return '-';
    const isWireless = this.isSensorWireless(this.sensorInfoDialogData.sensorType);
    return isWireless
      ? this.$t('sensorInfo.wireless').toString()
      : this.$t('sensorInfo.wired').toString();
  }

  /** Maps a ComBoxSensorType to the corresponding i18n type key. */
  private getSensorTypeKey(type: ComBoxSensorType): string | null {
    switch (type) {
      case ComBoxSensorType.TELAIRE:
      case ComBoxSensorType.SHT3X:
      case ComBoxSensorType.BLE_SENSOR_TYPE_1:
      case ComBoxSensorType.BLE_SENSOR_TYPE_5:
        return 'typeTempHumidity';
      case ComBoxSensorType.BLE_SENSOR_TYPE_3:
      case ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL:
        return 'typeHammerElectrode';
      case ComBoxSensorType.RADON:
        return 'typeRadon';
      // TODO: Add PRESSURE and HAMMER_ELECTRODE types when ComBoxSensorType is extended
      default:
        return null;
    }
  }

  /** Returns true if the sensor type is wireless (BLE). */
  private isSensorWireless(type: ComBoxSensorType): boolean {
    return type === ComBoxSensorType.BLE_SENSOR_TYPE_1
      || type === ComBoxSensorType.BLE_SENSOR_TYPE_5
      || isBleResistanceSensorType(type)
      || type === ComBoxSensorType.RADON;
  }

  getCellColor(cell: Cell) {
    this.selectionChangeTrigger;

    if (!this.isCellSelected(cell)) {
      return "";
    }

    const sensor = this.selectionOptionsSensor[cell.sensorIndex];
    const sensorValue = this.selectionOptionsSensorValue[cell.sensorValueIndex];

    return getIresDeviceSensorColor(sensor, sensorValue);
  }

  /** Emits highlight colors for all selected value types of the hovered sensor column. */
  onColumnHeaderHover(cell: Cell) {
    const sensorIndex = cell.columnIndex - 1;
    const sensor = this.selectionOptionsSensor[sensorIndex];
    const colors: string[] = [];
    for (let vi = 0; vi < this.selectionOptionsSensorValue.length; vi++) {
      if (this.selection[sensorIndex]?.[vi]) {
        colors.push(getIresDeviceSensorColor(sensor, this.selectionOptionsSensorValue[vi]));
      }
    }
    this.$emit("onLegendHover", colors.length > 0 ? colors : null);
  }

  /**
   * Emits highlight colors for the hovered value type (e.g. temperature) across every sensor.
   *
   * Spans all sensors, not just the columns shown, to match what clicking the row header toggles:
   * in overlay mode the grid is capped at OVERLAY_MAX_SENSOR_COLUMNS but the hidden sensors' series
   * are still drawn in the graph.
   */
  onRowHeaderHover(cell: Cell) {
    const sensorValueIndex = cell.rowIndex - 1;
    const sensorValue = this.selectionOptionsSensorValue[sensorValueIndex];
    const colors: string[] = [];
    this.selectionOptionsSensor.forEach((sensor, sensorIndex) => {
      if (
        this.selection[sensorIndex]?.[sensorValueIndex] &&
        !this.isSensorCombinationDisabled(sensorIndex, sensorValueIndex)
      ) {
        colors.push(getIresDeviceSensorColor(sensor, sensorValue));
      }
    });
    this.$emit("onLegendHover", colors.length > 0 ? colors : null);
  }

  /** Emits highlight color for a specific sensor+value dot, only if selected. */
  onDotHover(cell: Cell) {
    if (!this.selection[cell.sensorIndex]?.[cell.sensorValueIndex]) {
      return;
    }
    const sensor = this.selectionOptionsSensor[cell.sensorIndex];
    const sensorValue = this.selectionOptionsSensorValue[cell.sensorValueIndex];
    const color = getIresDeviceSensorColor(sensor, sensorValue);
    this.$emit("onLegendHover", [color]);
  }

  /** Clears the legend hover highlight. */
  onLegendHoverEnd() {
    this.isWeatherHovered = false;
    this.isPowerHovered = false;
    this.$emit("onLegendHover", null);
  }

  /** Emits iresDatasetType identifiers for all active weather datasets. */
  onWeatherHover() {
    this.isWeatherHovered = true;
    const types: string[] = ['weather-temp', 'weather-humidity'];
    if (this.weatherInformationDetailsLevel === WeatherInformationDetailsLevel.ALL) {
      types.push('weather-pressure', 'weather-precipitation');
    }
    this.$emit("onLegendHover", types);
  }

  /** Emits iresDatasetType identifier for the power consumption dataset. */
  onPowerHover() {
    this.isPowerHovered = true;
    this.$emit("onLegendHover", ['power']);
  }

  /** Maximum sensor columns shown in overlay mode to keep the legend compact. */
  private static readonly OVERLAY_MAX_SENSOR_COLUMNS = 5;

  /** Sensors to display in the grid. In overlay mode with many sensors, shows a prioritized subset. */
  get displayedSensors(): IresDeviceSensor[] {
    if (this.legendDisplayMode !== LegendDisplayMode.OVERLAY ||
        this.selectionOptionsSensor.length <= SelectionTableComponent.OVERLAY_MAX_SENSOR_COLUMNS) {
      return this.selectionOptionsSensor;
    }
    return this.selectionOptionsSensor.slice(0, SelectionTableComponent.OVERLAY_MAX_SENSOR_COLUMNS);
  }

  get cells() {
    const rows: Cell[][] = [];

    const numRows = this.selectionOptionsSensorValue.length + 1;
    const numCols = this.displayedSensors.length + 1;

    for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
      const col: Cell[] = [];

      for (let columnIndex = 0; columnIndex < numCols; columnIndex++) {
        col.push({
          columnIndex: columnIndex,
          rowIndex: rowIndex,
          sensorIndex: columnIndex - 1,
          sensorValueIndex: rowIndex - 1,
          key: -(rowIndex * numCols + columnIndex),
        });
      }

      rows.push(col);
    }

    return rows;
  }

  getSensorDescription(sensor: IresDeviceSensor) {
    const sensorIndex = getSensorIndexForIresDeviceSensor(sensor);
    if (sensorIndex !== null && sensorIndex === ROOM_SENSOR_INDEX) {
      return getIresDeviceSensorDescription(this, sensor);
    }
    if (sensorIndex !== null) {
      const slotLabel = formatSensorSlotLabel(
        sensorIndex,
        this.sensorTypeMap.get(sensorIndex),
        getSerialForSensorIndex(this.aggregatedSensorSlotSerials, sensorIndex),
        this.isMini,
      ) ?? `${calculateSlotNumberFromIndex(sensorIndex)}L`;
      return slotLabel;
    }
    return getIresDeviceSensorDescription(this, sensor);
  }

  getSensorValueDescription(sensorValue: IresDeviceSensorValue) {
    return getIresDeviceSensorValueDescription(this, sensorValue);
  }

  showSensorSelectionOptionsForProgramChain(
    programChain: ProgramChain,
    isAllowedToViewIntensity?: boolean,
  ) {
    this.chainId = programChain.id;

    const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
      programChain.serialNumber
    );

    if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
      this.showSensorSelectionOptionsForComBoxProgramChain(programChain);
    } else if (iresDeviceCategory === IresDeviceCategory.PANEL) {
      this.showSensorSelectionOptionsForPanelProgramChain(programChain, isAllowedToViewIntensity);
    }
  }

  showSensorSelectionOptionsForPanelProgramChain(
    programChain: ProgramChain,
    isAllowedToViewIntensity?: boolean,
  ) {
    const panelType = getPanelTypeFromSerialNumber(programChain.serialNumber);

    this.selectionOptionsSensor =
      panelType === PanelType.EDGE
        ? [IresDeviceSensor.PANEL_WALL, IresDeviceSensor.PANEL_GROUND]
        : [IresDeviceSensor.PANEL_WALL];

    this.selectionOptionsSensorValue = [
      IresDeviceSensorValue.TEMPERATURE
    ];

    if (isAllowedToViewIntensity) {
      this.selectionOptionsSensorValue.push(IresDeviceSensorValue.INTENSITY)
    }

    this.disabledCombinations.clear();
    this.disabledCombinations.add(
      iresSensorAndValueToKey({
        sensor: IresDeviceSensor.PANEL_GROUND,
        value: IresDeviceSensorValue.INTENSITY,
      })
    );

    this.selection = this.selectionOptionsSensor.map(() =>
      this.selectionOptionsSensorValue.map(() => true)
    );

    this.legendDisplayMode = LegendDisplayMode.OVERLAY;
    this.$emit("onLegendDisplayModeChanged", this.legendDisplayMode);

    this.onSelectionChanged();
  }

  showSensorSelectionOptionsForComBoxProgramChain(programChain: ProgramChain) {
    const sensors = new Set<IresDeviceSensor>();

    this.sensorUsageMap.clear();
    this.sensorTypeMap.clear();
    this.aggregatedSensorSlotSerials = {};
    const latestRun = programChain.runs[programChain.runs.length - 1];

    programChain.runs.forEach((run) => {
      run.sensorConfig.forEach((sensorType, index) => {
        if (
          sensorType !== ComBoxSensorType.EMPTY &&
          sensorType !== ComBoxSensorType.UNKNOWN
        ) {
          sensors.add(getComBoxSensorForSensorIndex(index));
        }
      });
    });

    this.aggregatedSensorSlotSerials = aggregateSensorSlotSerials(
      programChain.runs,
      this.activeFallbackSerials,
    );

    if (latestRun?.sensorUsage) {
      latestRun.sensorUsage.forEach((usage, index) => {
        const sensor = getComBoxSensorForSensorIndex(index);
        this.sensorUsageMap.set(sensor, usage);
      });
    }

    if (latestRun?.sensorConfig) {
      latestRun.sensorConfig.forEach((sensorType, index) => {
        if (sensorType !== ComBoxSensorType.EMPTY && sensorType !== ComBoxSensorType.UNKNOWN) {
          this.sensorTypeMap.set(index, sensorType);
        }
      });
    }

    this.disabledCombinations.clear();

    // Store natural sensor order (sorted by enum value / slot number)
    this.naturalSensorOrder = [...sensors].sort((a, b) => a - b);

    const hasResistanceDigitsValues = programChain.runs.some((run) =>
      run.samples.some((sample) => {
        const sensorData = (sample as { sensorData?: Array<{ resistanceDigits?: number }> }).sensorData;
        if (!sensorData) {
          return false;
        }

        return sensorData.some((entry) => entry?.resistanceDigits !== undefined);
      })
    );

    this.selectionOptionsSensorValue = [
      IresDeviceSensorValue.TEMPERATURE,
      IresDeviceSensorValue.RELATIVE_HUMIDITY,
    ];

    if (hasResistanceDigitsValues) {
      this.selectionOptionsSensorValue.push(IresDeviceSensorValue.RESISTANCE_DIGITS);
    }

    this.naturalSensorOrder.forEach((sensor) => {
      const sensorIndex = getSensorIndexForIresDeviceSensor(sensor);
      if (sensorIndex === null) {
        return;
      }

      const sensorType = latestRun?.sensorConfig?.[sensorIndex];
      if (!isBleResistanceSensorType(sensorType)) {
        return;
      }

      this.disabledCombinations.add(
        iresSensorAndValueToKey({
          sensor,
          value: IresDeviceSensorValue.TEMPERATURE,
        })
      );
      this.disabledCombinations.add(
        iresSensorAndValueToKey({
          sensor,
          value: IresDeviceSensorValue.RELATIVE_HUMIDITY,
        })
      );
    });

    if (hasResistanceDigitsValues) {
      this.naturalSensorOrder.forEach((sensor) => {
        const sensorIndex = getSensorIndexForIresDeviceSensor(sensor);
        if (sensorIndex === null) {
          return;
        }

        const sensorType = latestRun?.sensorConfig?.[sensorIndex];
        const isMaterialSensor = isBleResistanceSensorType(sensorType);

        if (!isMaterialSensor) {
          this.disabledCombinations.add(
            iresSensorAndValueToKey({
              sensor,
              value: IresDeviceSensorValue.RESISTANCE_DIGITS,
            })
          );
        }
      });
    }

    // Track external sensor count and set default legend mode
    this.externalSensorCount = this.naturalSensorOrder.filter((sensor) => {
      const index = getSensorIndexForIresDeviceSensor(sensor);
      return index !== null && index !== ROOM_SENSOR_INDEX;
    }).length;
    this.legendDisplayMode = this.externalSensorCount > 4
      ? LegendDisplayMode.BELOW
      : LegendDisplayMode.OVERLAY;

    // Apply sort order based on display mode and initialize selection
    this.applySensorSortOrder();
    this.$emit("onLegendDisplayModeChanged", this.legendDisplayMode);

    this.onSelectionChanged();
  }

  /**
   * Re-sorts selectionOptionsSensor based on the current legend display mode
   * and rebuilds the selection array to preserve toggle states.
   * OVERLAY mode uses priority-based sorting (important sensors first).
   * BELOW/HIDDEN mode uses natural slot order (enum value).
   */
  private applySensorSortOrder() {
    if (this.naturalSensorOrder.length === 0) return;

    // Save current selection state keyed by sensor
    const selectionState = new Map<IresDeviceSensor, boolean[]>();
    this.selectionOptionsSensor.forEach((sensor, index) => {
      if (this.selection[index]) {
        selectionState.set(sensor, this.selection[index]);
      }
    });

    // Sort by priority for overlay mode with >4 external sensors (columns are truncated), natural order otherwise
    if (this.legendDisplayMode === LegendDisplayMode.OVERLAY && this.externalSensorCount > 4) {
      this.selectionOptionsSensor = [...this.naturalSensorOrder].sort((a, b) =>
        this.sensorSortPriority(a) - this.sensorSortPriority(b)
      );
    } else {
      this.selectionOptionsSensor = [...this.naturalSensorOrder];
    }

    // Rebuild selection array in new order, preserving toggle states
    this.selection = this.selectionOptionsSensor.map((sensor) =>
      selectionState.get(sensor) ?? this.selectionOptionsSensorValue.map(() => true)
    );
  }

  /**
   * Returns a numeric priority for sorting sensors in the legend.
   * Order: ROOM first, then REF, then PROG (ascending slot), then MESS (ascending slot).
   * Within the same usage group, sensors are ordered by their natural enum value (slot number).
   */
  private sensorSortPriority(sensor: IresDeviceSensor): number {
    if (sensor === IresDeviceSensor.COM_BOX_ROOM_SENSOR) {
      return -1;
    }

    const usage = this.sensorUsageMap.get(sensor);
    let usagePriority: number;
    switch (usage) {
      case "REFERENCE": usagePriority = 0; break;
      case "PROGRAM": usagePriority = 1; break;
      case "MEASUREMENT": usagePriority = 2; break;
      default: usagePriority = 3; break;
    }

    return usagePriority * 100 + sensor;
  }
}
